import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";
import Dialog from "components/dialog";
import BoxFolderPicker, {
  FolderObject
} from "components/folder-picker/folder-picker.component";
import { SAVE_TO_EDOCS_NAMING_FEATURE } from "constants/feature-flags";

import {
  EDOCS_FILE_NAME_MAX_LENGTH,
  describeTypedName,
  edocsFileName,
  withoutExtension
} from "./edocs-file-name.util";
import LegacySaveToEdocsDialog from "./legacy-save-to-edocs-dialog.component";

export interface SaveToEdocsDialogProps {
  open: boolean;
  /**
   * The patient's E-Docs folder: where the picker starts, and where "no folder" saves. The picker
   * shows once it and the Box token are both known; until then a spinner holds its place.
   */
  folderId?: string | null;
  token?: string | null;
  /** The name the surface would have filed the document under. The field opens with it. */
  suggestedFileName: string;
  /**
   * The document's extension, shown after the field and always kept: ".pdf" (the default) for a
   * form the app makes. Null when the name carries no extension of its own.
   */
  extension?: string | null;
  /**
   * More than one document saved by one folder pick: each keeps its own name, so there is no name
   * to edit and the dialog says so instead.
   */
  documentCount?: number;
  /**
   * The picker's Choose Folder: the folder picked (null when none was, which saves to `folderId`)
   * and the file name to upload under, extension included.
   */
  onChoose: (
    folder: FolderObject | null,
    fileName: string
  ) => void | Promise<void>;
  onClose: () => void;
  /**
   * With SAVE_TO_EDOCS_NAMING_FEATURE off: the surface had a plain "Document Name" field above the
   * picker before this dialog (`useSaveToEdocs`), so keep it. Without it, the picker shows alone.
   */
  legacyNameField?: boolean;
}

const stepHeadingSx = { fontWeight: 700 };

/**
 * SN-20071: every "Upload to E-Docs" goes through this dialog. It asks for the document's name
 * (opening with the name the surface suggests, so a save that changes nothing files it exactly as
 * before), then for the folder, through the Box folder picker whose Choose Folder saves.
 *
 * With SAVE_TO_EDOCS_NAMING_FEATURE off it renders what each surface showed before it: the bare
 * picker, uploading under the surface's own name, or (`legacyNameField`) that picker under the plain
 * "Document Name" field `useSaveToEdocs` had.
 */
export default function SaveToEdocsDialog(props: SaveToEdocsDialogProps) {
  const namingEnabled = Boolean(useFlags()[SAVE_TO_EDOCS_NAMING_FEATURE]);
  return namingEnabled ? (
    <SaveToEdocsNamingDialog {...props} />
  ) : (
    <LegacySaveToEdocsDialog {...props} />
  );
}

function SaveToEdocsNamingDialog({
  open,
  folderId,
  token,
  suggestedFileName,
  extension = ".pdf",
  documentCount = 1,
  onChoose,
  onClose
}: SaveToEdocsDialogProps) {
  const [typedName, setTypedName] = useState("");
  // The field opens with the suggestion each time the dialog opens, and keeps what was typed while
  // it stays open: a surface that names its file by the clock suggests a new name every render.
  const [suggestionAtOpen, setSuggestionAtOpen] = useState<string | null>(null);
  if (open && suggestionAtOpen === null) {
    setSuggestionAtOpen(suggestedFileName);
    setTypedName(withoutExtension(suggestedFileName, extension));
  } else if (!open && suggestionAtOpen !== null) {
    setSuggestionAtOpen(null);
  }
  const suggestion = suggestionAtOpen ?? suggestedFileName;
  const isBatch = documentCount > 1;

  const handleChoose = useCallback(
    (folder: FolderObject | null) =>
      onChoose(
        folder,
        edocsFileName({
          typedName: isBatch ? "" : typedName,
          suggestedFileName: suggestion,
          extension
        })
      ),
    [extension, isBatch, onChoose, suggestion, typedName]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Save to E-Docs"
      minWidth={900}
      maxWidth="md"
      hideBackdrop
      sx={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      // The name step is a tinted band that starts right under the title, so the theme's
      // standard top padding (set with !important) is taken off here.
      dialogContentProps={{ sx: { p: 0, paddingTop: "0 !important" } }}
    >
      <DocumentNameStep
        documentCount={documentCount}
        typedName={typedName}
        onTypedNameChange={setTypedName}
        suggestedFileName={suggestion}
        extension={extension}
      />
      <Stack spacing={0.5} sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography variant="body2" sx={stepHeadingSx}>
          2. Choose the folder
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Click a folder, then Choose Folder to save. Choose Folder with no
          folder picked saves it in the patient&apos;s main E-Docs folder.
        </Typography>
      </Stack>
      {open && folderId && token ? (
        <BoxFolderPicker
          folderId={folderId}
          token={token}
          setSelectedFolder={handleChoose}
          onClose={onClose}
        />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={600}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
}

interface DocumentNameStepProps {
  documentCount: number;
  typedName: string;
  onTypedNameChange: (typedName: string) => void;
  suggestedFileName: string;
  extension: string | null;
}

/** Step 1: the name field, or, for several documents at once, why there is none. */
function DocumentNameStep({
  documentCount,
  typedName,
  onTypedNameChange,
  suggestedFileName,
  extension
}: DocumentNameStepProps) {
  const isBatch = documentCount > 1;
  return (
    <Stack
      data-testid="save-to-edocs-name-step"
      spacing={1.5}
      sx={{ backgroundColor: neutral[50], px: 3, py: 2.5 }}
    >
      <Typography variant="body2" sx={stepHeadingSx}>
        {isBatch ? "1. Document names" : "1. Name the document"}
      </Typography>
      {isBatch ? (
        <Typography variant="body1">
          {documentCount} documents will be saved to the folder you choose, each
          under its own name.
        </Typography>
      ) : (
        <>
          <Typography variant="body1" color="text.secondary">
            Keep the suggested name or type your own, for example with the
            device, side or repair number, so it is easy to find later.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="Document name"
            value={typedName}
            onChange={(event) => onTypedNameChange(event.target.value)}
            onFocus={(event) => {
              const { length } = event.target.value;
              event.target.setSelectionRange(length, length);
            }}
            helperText={describeTypedName({
              typedName,
              suggestedFileName,
              extension
            })}
            FormHelperTextProps={{ sx: { typography: "body1", mx: 0 } }}
            inputProps={{
              maxLength: EDOCS_FILE_NAME_MAX_LENGTH - (extension?.length ?? 0),
              "data-testid": "save-to-edocs-name-input"
            }}
            InputProps={{
              endAdornment: extension ? (
                <InputAdornment position="end">{extension}</InputAdornment>
              ) : undefined
            }}
            sx={{
              "& .MuiInputBase-root": { backgroundColor: "common.white" }
            }}
          />
        </>
      )}
    </Stack>
  );
}
